/**
 * <coating-slider-el>
 *
 * Centered Swiper with two-image slides.
 * Tab navigation at top syncs with active slide and vice-versa.
 */
class CoatingSlider extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;
  /** Prevents slideChange during loop-init from overwriting tabs */
  #ready = false;

  connectedCallback() {
    this.#init();

    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.contains(this)) {
        this.#swiper?.destroy(true, true);
        this.#swiper = null;
        this.#ready = false;
        this.#init();
      }
    }, { once: true });
  }

  disconnectedCallback() {
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  #init() {
    const swiperEl = this.querySelector('.cslider__swiper');
    if (!swiperEl) return;

    const prevBtn   = this.querySelector('.cslider__nav-btn--prev');
    const nextBtn   = this.querySelector('.cslider__nav-btn--next');
    const captions  = this.querySelectorAll('.cslider__caption');
    const dots      = this.querySelectorAll('.cslider__dot');
    const dotsIndicator = this.querySelector('.cslider__dots-indicator');

    // Tabs live outside this custom element (in the section header)
    const section = this.closest('.cslider');
    const tabs = section?.querySelectorAll('.cslider__tab');
    const indicator = section?.querySelector('.cslider__tab-indicator');

    this.#swiper = new Swiper(swiperEl, {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 0,
      loop: true,
      loopAdditionalSlides: 3,
      breakpoints: {
        0: {
          slidesPerView: 1,
          centeredSlides: false,
          spaceBetween: 0,
        },
        750: {
          slidesPerView: 'auto',
          centeredSlides: true,
          spaceBetween: 0,
        },
      },
      navigation: {
        prevEl: prevBtn,
        nextEl: nextBtn,
        disabledClass: 'swiper-button-disabled',
      },
      on: {
        afterInit: (swiper) => {
          const realIdx = this.#getRealIndex(swiper);
          this.#syncActive(realIdx, tabs, captions, dots, dotsIndicator, indicator);
          this.#ready = true;
        },
        slideChange: (swiper) => {
          if (!this.#ready) return;
          const realIdx = this.#getRealIndex(swiper);
          this.#syncActive(realIdx, tabs, captions, dots, dotsIndicator, indicator);
        },
      },
    });

    // Tab → slide
    tabs?.forEach((tab) => {
      tab.addEventListener('click', () => {
        const idx = parseInt(tab.dataset.index, 10);
        this.#swiper?.slideTo(3 + idx, 300);
      });
    });

    // Dot → slide
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.index, 10);
        this.#swiper?.slideTo(3 + idx, 300);
      });
    });
  }

  /**
   * Read real index from active slide's data-real-index attribute.
   * This works with 9 duplicated slides (3 copies × 3 slides).
   * @param {import('swiper').Swiper} swiper
   * @returns {number} 0-2
   */
  #getRealIndex(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return 0;
    return parseInt(activeSlide.dataset.realIndex ?? '0', 10);
  }

  /**
   * @param {number} idx
   * @param {NodeList|undefined} tabs
   * @param {NodeList} captions
   */
  #syncActive(idx, tabs, captions, dots, dotsIndicator, indicator) {
    tabs?.forEach((tab, i) => {
      tab.classList.toggle('is-active', i === idx);
      tab.setAttribute('aria-selected', i === idx ? 'true' : 'false');
    });
    captions.forEach((cap, i) => {
      cap.classList.toggle('is-active', i === idx);
    });
    // Slide the dots indicator
    if (dotsIndicator) {
      dotsIndicator.style.transform = `translateX(${idx * 60}px)`;
    }
    // Move sliding underline indicator to active tab
    // offsetLeft is relative to offsetParent (the nav), unaffected by scroll
    if (indicator && tabs?.[idx]) {
      const activeTab = tabs[idx];
      indicator.style.left = activeTab.offsetLeft + 'px';
      indicator.style.width = activeTab.offsetWidth + 'px';

      // Auto-scroll the nav so the active tab is fully visible
      const nav = activeTab.closest('.cslider__tabs');
      if (nav) {
        const tabLeft = activeTab.offsetLeft;
        const tabRight = tabLeft + activeTab.offsetWidth;
        const navVisible = nav.scrollLeft + nav.clientWidth;
        if (tabLeft < nav.scrollLeft) {
          nav.scrollTo({ left: tabLeft - 16, behavior: 'smooth' });
        } else if (tabRight > navVisible) {
          nav.scrollTo({ left: tabRight - nav.clientWidth + 16, behavior: 'smooth' });
        }
      }
    }
  }
}

customElements.define('coating-slider-el', CoatingSlider);
